// ===========================================================================
// NoRecentVisitsPage — entry point del módulo No Recent Visits.
//
// Lista informativa y de priorización: identifica pacientes según el tiempo
// transcurrido desde su última visita. Sin acciones de workflow todavía (Fase 1).
//
// Orquesta 3 endpoints con los MISMOS filtros para que tiles, conteo y lista
// reflejen el mismo conjunto:
//   - cambia un filtro  → kpis + count + lista (página 1) en paralelo
//   - cambia de página  → solo la lista (kpis y count no cambian)
//
// Filtros: búsqueda libre (debounce 400ms, aplica al instante), panel de
// filtros con Apply/Clear, y donuts clickeables como atajos. Chips removibles
// muestran el estado activo.
// ===========================================================================
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Container,
  Box,
  Paper,
  Stack,
  Typography,
  Breadcrumbs,
  Link,
  TextField,
  InputAdornment,
  IconButton,
  Button,
  Badge,
  Alert,
  Tooltip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import TuneIcon from "@mui/icons-material/Tune";
import RefreshIcon from "@mui/icons-material/Refresh";

import KpiTiles from "../components/Kpis/KpiTiles";
import FilterDrawer from "../components/Filters/FilterDrawer";
import ActiveFilterChips from "../components/Filters/ActiveFilterChips";
import NoRecentVisitsTable from "../components/Table/NoRecentVisitsTable";
import PatientDetailDrawer from "../components/Detail/PatientDetailDrawer";

import {
  getNoRecentVisits,
  getNoRecentVisitsCount,
  getNoRecentVisitsKpis,
} from "../services";
import { getLocations } from "../../../utils/services/control/masterDataService";
import { DEFAULT_PAGE_SIZE } from "../common/constants";

const EMPTY_FILTERS = {
  search: null,
  dob: null,
  locationId: null,
  pcpName: null,
  networkName: null,
  metalLevel: null,
  timeBucket: null,
};

// Cuenta filtros activos (sin la búsqueda libre, que tiene su propio control).
const countPanelFilters = (f) =>
  ["dob", "locationId", "pcpName", "networkName", "metalLevel", "timeBucket"].filter((k) => f[k]).length;

export default function NoRecentVisitsPage() {
  const [appliedFilters, setAppliedFilters] = useState({ ...EMPTY_FILTERS });
  const [page, setPage] = useState(0); // 0-based (DataGrid)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [kpis, setKpis] = useState(null);
  const [networkCatalog, setNetworkCatalog] = useState([]);
  const [locationMap, setLocationMap] = useState({});

  const [listLoading, setListLoading] = useState(false);
  const [aggLoading, setAggLoading] = useState(false);
  const [listError, setListError] = useState(null);

  const [searchInput, setSearchInput] = useState("");
  const [filterOpen, setFilterOpen] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  // Catálogos estables (una sola carga): networks sin filtrar (para el dropdown
  // del panel) y mapa location id → nombre (para los chips).
  useEffect(() => {
    let active = true;
    getNoRecentVisitsKpis({})
      .then((k) => active && setNetworkCatalog(k?.networks || []))
      .catch(() => {});
    getLocations({ status: 1, clinical: true })
      .then((locs) => {
        if (!active) return;
        const map = {};
        (Array.isArray(locs) ? locs : []).forEach((l) => {
          map[l.locationId] = l.medicalCenter || l.centerKey || `Location ${l.locationId}`;
        });
        setLocationMap(map);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, []);

  // Lista — depende de filtros + paginación.
  useEffect(() => {
    let active = true;
    setListLoading(true);
    setListError(null);
    getNoRecentVisits({ ...appliedFilters, page: page + 1, pageSize })
      .then((res) => {
        if (!active) return;
        setRows(Array.isArray(res?.data) ? res.data : []);
      })
      .catch(() => {
        if (!active) return;
        setListError("There was an error fetching No Recent Visits. Please try again.");
        setRows([]);
      })
      .finally(() => active && setListLoading(false));
    return () => {
      active = false;
    };
  }, [appliedFilters, page, pageSize]);

  // KPIs + conteo — dependen solo de los filtros (no de la página).
  useEffect(() => {
    let active = true;
    setAggLoading(true);
    Promise.all([
      getNoRecentVisitsKpis(appliedFilters),
      getNoRecentVisitsCount(appliedFilters),
    ])
      .then(([k, t]) => {
        if (!active) return;
        setKpis(k);
        setTotal(Number(t) || 0);
      })
      .catch(() => {
        if (!active) return;
        setKpis(null);
        setTotal(0);
      })
      .finally(() => active && setAggLoading(false));
    return () => {
      active = false;
    };
  }, [appliedFilters]);

  // Aplica filtros reseteando a la página 1 (todo cambio de filtro lo hace).
  const applyFilters = useCallback((next) => {
    setAppliedFilters(next);
    setPage(0);
  }, []);

  // Búsqueda libre con debounce de 400ms.
  const debounceRef = useRef(null);
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      const value = searchInput.trim();
      setAppliedFilters((prev) => {
        const nextSearch = value.length >= 2 ? value : null;
        if (prev.search === nextSearch) return prev;
        setPage(0);
        return { ...prev, search: nextSearch };
      });
    }, 400);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [searchInput]);

  const handleApplyPanel = (draft) => {
    applyFilters({ ...appliedFilters, ...draft });
    setFilterOpen(false);
  };

  const handleClearPanel = () => {
    applyFilters({ ...EMPTY_FILTERS, search: appliedFilters.search });
  };

  const handleRemoveChip = (key) => {
    if (key === "search") {
      setSearchInput("");
      applyFilters({ ...appliedFilters, search: null });
    } else {
      applyFilters({ ...appliedFilters, [key]: null });
    }
  };

  const handleClearAll = () => {
    setSearchInput("");
    applyFilters({ ...EMPTY_FILTERS });
  };

  const handleRowClick = (row) => {
    setSelectedPatient(row);
    setDetailOpen(true);
  };

  const onPaginationChange = (model) => {
    if (model.pageSize !== pageSize) {
      setPageSize(model.pageSize);
      setPage(0); // cambiar pageSize resetea a página 1
    } else {
      setPage(model.page);
    }
  };

  const panelFilterCount = countPanelFilters(appliedFilters);
  const networksForPanel = networkCatalog.length ? networkCatalog : kpis?.networks || [];
  const locationName = useMemo(() => (id) => locationMap[id], [locationMap]);

  const refresh = () => setAppliedFilters((f) => ({ ...f })); // re-dispara ambos effects

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* [1] Breadcrumb + header */}
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", md: "center" }}
        spacing={2}
        sx={{ mb: 2 }}
      >
        <Box>
          <Breadcrumbs sx={{ mb: 0.5 }}>
            <Link underline="hover" color="text.secondary" href="/Main">
              Dashboard
            </Link>
            <Typography color="text.primary">No Recent Visits</Typography>
          </Breadcrumbs>
          <Typography variant="h4" fontWeight={900}>
            No Recent Visits
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Patients prioritized by time since their last known medical visit.
          </Typography>
        </Box>

        <Stack direction="row" spacing={1} alignItems="center">
          <TextField
            size="small"
            placeholder="Search by Member ID, Name, CIP..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            sx={{ minWidth: { xs: "100%", md: 360 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: searchInput ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchInput("")} aria-label="Clear search">
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
          <Badge badgeContent={panelFilterCount} color="primary">
            <Button
              variant="outlined"
              startIcon={<TuneIcon />}
              onClick={() => setFilterOpen(true)}
              sx={{ textTransform: "none" }}
            >
              Filters
            </Button>
          </Badge>
          <Tooltip title="Refresh">
            <IconButton onClick={refresh} disabled={listLoading || aggLoading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      {/* [2] KPI tiles */}
      <KpiTiles
        kpis={kpis}
        loading={aggLoading && !kpis}
        activeNetwork={appliedFilters.networkName}
        activeTimeBucket={appliedFilters.timeBucket}
        onSelectNetwork={(v) => applyFilters({ ...appliedFilters, networkName: v })}
        onSelectTimeBucket={(v) => applyFilters({ ...appliedFilters, timeBucket: v })}
      />

      {/* [3] Filter chips */}
      <ActiveFilterChips
        filters={appliedFilters}
        locationName={locationName}
        onRemove={handleRemoveChip}
        onClearAll={handleClearAll}
      />

      {/* [4] Tabla */}
      <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5, borderRadius: 3 }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="body2" fontWeight={700}>
            {aggLoading ? "Loading…" : `${total.toLocaleString()} patients`}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            · sorted by name (A–Z)
          </Typography>
        </Stack>
      </Paper>

      {listError && (
        <Alert severity="error" sx={{ mb: 2 }} action={<Button color="inherit" size="small" onClick={refresh}>Retry</Button>}>
          {listError}
        </Alert>
      )}

      <NoRecentVisitsTable
        rows={rows}
        rowCount={total}
        loading={listLoading}
        page={page}
        pageSize={pageSize}
        onPaginationChange={onPaginationChange}
        onRowClick={handleRowClick}
      />

      <FilterDrawer
        open={filterOpen}
        value={appliedFilters}
        networks={networksForPanel}
        onClose={() => setFilterOpen(false)}
        onApply={handleApplyPanel}
        onClear={handleClearPanel}
      />

      <PatientDetailDrawer
        patient={selectedPatient}
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
      />
    </Container>
  );
}
